import { useEffect, useState } from "react";
import { formatAppointmentDateDisplay, formatAppointmentTimeDisplay } from '../../utils/appointmentDate';
import BotonSubmitEpayco from "../ePayco/BotonSubmitEpayco";
import { Link, useNavigate } from "react-router-dom";
import { useDataUser } from "../../context/dataUserContext";
import apiClient from "../../utils/axiosPetition";
import { alertaGeneral } from "../../utils/alertas";
import Cargando from "../../utils/Cargando";
import SvgIcon from "../../utils/Icon";
import { ReactComponent as AgendarIcon } from "../../assets/icons/agendar.svg";
import { ReactComponent as CalendarioIcon } from "../../assets/icons/calendario.svg";
import { ReactComponent as UsuarioIcon } from "../../assets/icons/usuario.svg";
import { APP_URLS } from "../../config/appUrls";

const AgendarCita = () => {
    const preInfo = JSON.parse(localStorage.getItem('selectedDate')) || {};
    const [userEmail, setUserEmail] = useState('');
    const [error, setError] = useState(true);
    const navigate = useNavigate();
    const { role, fullName, patientId } = useDataUser();
    const [aceptaPoliticas, setAceptaPoliticas] = useState(false);
    const [intentoEnviar, setIntentoEnviar] = useState(false);
    const [precioCita, setPrecioCita] = useState('');
    const [doctorInfo, setDoctorInfo] = useState(null);
    const [tipoCita, setTipoCita] = useState(preInfo.tipoCita || '');

    if (role !== 'patient' && role !== 'doctor') {
        navigate('/');
    }

    const [dataCita, setDataCita] = useState({
        doctorId: preInfo.doctorId || '',
        doctor: preInfo.doctor || '',
        servicio: preInfo.servicio || '',
        precioServicio: preInfo.precioServicio || '',
        fecha: preInfo.fecha || '',
        tipoPagador: preInfo.tipoPagador || 'particular',
        tipoConvenio: preInfo.tipoConvenio || '',
        motivoConsulta: preInfo.motivoConsulta || '',
        tipoCita: preInfo.tipoCita || '', // Nuevo campo para tipo de cita
    });

    // Función para normalizar el servicio
    const normalizeService = (service) => {
        if (!service) return '';
        return service.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
            .replace(/\d+/g, '') // Quitar números
            .replace(/_/g, ' ') // Reemplazar guiones bajos
            .replace(/-/g, ' ') // Reemplazar guiones
            .trim();
    }

    // Función para obtener la configuración de precios según la especialidad
    const getPreciosConfig = () => {
        const service = dataCita.servicio || doctorInfo?.service;
        if (!service) return null;
        
        const serviceNormalized = normalizeService(service);
        
        // Configuración de precios por especialidad según la tabla
        const preciosConfig = {
            // Medicina familiar: 100k-120k / 90k / 50k
            'medicina familiar': {
                precioControles: 90000,
                precioTeleorientacion: 50000
            },
            // Medicina General: 60k-100k / 60k / 45k
            'medicina general': {
                precioControles: 60000,
                precioTeleorientacion: 45000
            },
            // Terapia Física: 30k-50k / 40k / 25k
            'terapia fisica': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            // Terapia de lenguaje: 30k-50k / 40k / 25k
            'terapia de lenguaje': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            'terapia lenguaje': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            'fonoaudiologia': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            // Terapia Ocupacional / Terapeuta Ocupacional: 30k-50k / 40k / 25k
            'terapia ocupacional': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            'terapeuta ocupacional': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            // Terapia respiratoria: 30k-50k / 40k / 25k
            'terapia respiratoria': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            'terapeuta respiratorio': {
                precioControles: 40000,
                precioTeleorientacion: 25000
            },
            // Psicología: 50k-100k / 60k / 25k
            'psicologia': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            'psicologo': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            // Trabajo social: 50k-100k / 60k / 25k
            'trabajo social': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            // Nutrición: 50k-100k / 60k / 25k
            'nutricion': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            'nutricionista': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            // Enfermería: 50k-100k / 60k / 25k
            'enfermeria': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            'enfermero': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            },
            'enfermera': {
                precioControles: 60000,
                precioTeleorientacion: 25000
            }
        };

        // Buscar coincidencia en la configuración
        for (const [key, config] of Object.entries(preciosConfig)) {
            if (serviceNormalized.includes(key)) {
                return config;
            }
        }

        return null;
    }

    // Función para verificar si la especialidad requiere configuración de precios
    const requiereConfiguracionPrecios = () => {
        return getPreciosConfig() !== null;
    }

    useEffect(() => {
        const dataToSave = {
            ...dataCita,
            tipoCita: tipoCita || dataCita.tipoCita
        };
        localStorage.setItem('selectedDate', JSON.stringify(dataToSave));
    }, [dataCita, tipoCita]);

    // Actualizar precio cuando cambia el tipo de cita
    useEffect(() => {
        if (requiereConfiguracionPrecios() && doctorInfo?.preciosCitas && tipoCita) {
            const preciosConfig = getPreciosConfig();
            let precio = 0;
            switch (tipoCita) {
                case 'teleconsulta_primera_vez':
                    precio = doctorInfo.preciosCitas.teleconsultaPrimeraVez || 0;
                    break;
                case 'controles':
                    precio = doctorInfo.preciosCitas.precioControles || preciosConfig?.precioControles || 0;
                    break;
                case 'teleorientacion':
                    precio = doctorInfo.preciosCitas.precioTeleorientacion || preciosConfig?.precioTeleorientacion || 0;
                    break;
                default:
                    precio = 0;
            }
            setPrecioCita(precio);
            setDataCita(prev => ({ ...prev, precioServicio: precio, tipoCita }));
        }
    }, [tipoCita, doctorInfo]);

    useEffect(() => {
        const hasTipoCita = !requiereConfiguracionPrecios() || tipoCita !== '';
        if (dataCita.motivoConsulta == '' || dataCita.tipoPagador.length == 0 || (dataCita.tipoPagador === 'convenio' && dataCita.tipoConvenio.length === 0) || !hasTipoCita) {
            setError(true);
        } else {
            setError(false);
        }
    }, [dataCita, tipoCita]);

    useEffect(() => {
        apiClient.get('/api/users/getEmail')
            .then(res => {
                setUserEmail(res.data.email);
            })
            .catch(err => {
                alertaGeneral({
                    titulo: 'Ocurrió un error al obtener su email, intente nuevamente',
                    isError: true
                }).then(result => {
                    navigate('../teleconsultas')
                })
            })
    },[])

    // Obtener información del doctor
    useEffect(() => {
        if (dataCita.doctorId) {
            apiClient.get(`/api/doctor/${dataCita.doctorId}`)
                .then(res => {
                    const doctorData = res.data;
                    setDoctorInfo(doctorData);
                    
                    // Actualizar servicio en dataCita si no está presente o si viene del backend
                    if (!dataCita.servicio && doctorData.service) {
                        setDataCita(prev => ({ ...prev, servicio: doctorData.service }));
                    }
                    
                    // Verificar si requiere configuración de precios usando los datos recibidos directamente
                    const service = dataCita.servicio || doctorData.service;
                    const serviceNormalized = normalizeService(service);
                    const preciosConfig = getPreciosConfig();
                    const requierePrecios = requiereConfiguracionPrecios();
                    
                    // Si requiere configuración de precios, mostrar las opciones y seleccionar una por defecto
                    if (requierePrecios && preciosConfig) {
                        // Si tiene precios configurados y no hay tipoCita seleccionado, usar el primero disponible
                        if (doctorData.preciosCitas && !tipoCita) {
                            if (doctorData.preciosCitas.teleconsultaPrimeraVez) {
                                setTipoCita('teleconsulta_primera_vez');
                                setPrecioCita(doctorData.preciosCitas.teleconsultaPrimeraVez);
                                setDataCita(prev => ({ 
                                    ...prev, 
                                    precioServicio: doctorData.preciosCitas.teleconsultaPrimeraVez,
                                    tipoCita: 'teleconsulta_primera_vez',
                                    servicio: prev.servicio || doctorData.service
                                }));
                            } else if (doctorData.preciosCitas.precioControles) {
                                setTipoCita('controles');
                                const precioControles = doctorData.preciosCitas.precioControles || preciosConfig.precioControles;
                                setPrecioCita(precioControles);
                                setDataCita(prev => ({ 
                                    ...prev, 
                                    precioServicio: precioControles,
                                    tipoCita: 'controles',
                                    servicio: prev.servicio || doctorData.service
                                }));
                            } else if (doctorData.preciosCitas.precioTeleorientacion) {
                                setTipoCita('teleorientacion');
                                const precioTeleorientacion = doctorData.preciosCitas.precioTeleorientacion || preciosConfig.precioTeleorientacion;
                                setPrecioCita(precioTeleorientacion);
                                setDataCita(prev => ({ 
                                    ...prev, 
                                    precioServicio: precioTeleorientacion,
                                    tipoCita: 'teleorientacion',
                                    servicio: prev.servicio || doctorData.service
                                }));
                            }
                        }
                    }
                })
                .catch(err => {
                    console.error('Error obteniendo información del doctor:', err);
                });
        }
    }, [dataCita.doctorId]);

    useEffect(() => {
        // Solo obtener precio general si NO requiere configuración de precios
        if (doctorInfo && !requiereConfiguracionPrecios()) {
            apiClient.get('/api/precios')
                .then(res => {
                    const precio = res.data.find(s => s.nombre.toLowerCase().includes('cita'));
                    setPrecioCita(precio?.valor || '');
                    setDataCita(prev => ({ ...prev, precioServicio: precio?.valor || '' }));
                });
        }
    }, [doctorInfo, dataCita.servicio]);

    const handleClickRegresar = () => {
        navigate("../teleconsultas");
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDataCita({ ...dataCita, [name]: value });
    };

    if(!userEmail){
        return <Cargando />
    }

    const inputBase = "w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50/80 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1f94c2]/40 focus:border-[#1f94c2] focus:bg-white transition-all duration-200";
    const inputReadOnly = "w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-100/90 text-slate-700";
    const doctorName = dataCita.doctor || doctorInfo?.fullName || 'Profesional';
    const doctorPhoto = doctorInfo?.urlProfessionalPhoto || doctorInfo?.urlPictureProfile || '';
    const specialty = dataCita.servicio || doctorInfo?.service || '';
    const fechaFormateada = formatAppointmentDateDisplay(dataCita.fecha);
    const horaFormateada = formatAppointmentTimeDisplay(dataCita.fecha);

    return (
        <div className="flex flex-col w-full max-w-4xl mx-auto min-h-0 py-2 sm:py-4 px-0.5 sm:px-2">
            <div className="flex-1 min-h-0 flex flex-col bg-white shadow-xl rounded-2xl border border-slate-200/80 overflow-hidden">
                {/* Cabecera */}
                <div className="bg-gradient-to-br from-[#1f94c2] via-[#205087] to-[#1a4d6e] px-3 py-5 sm:px-4 text-white shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 shrink-0">
                            <SvgIcon Icon={AgendarIcon} className="w-7 h-7 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight">Confirmar tu cita</h1>
                            <p className="text-white/90 text-sm mt-0.5 leading-snug">Revisa los datos y completa el formulario</p>
                        </div>
                    </div>
                </div>

                <form className="flex-1 min-h-0 flex flex-col gap-4 p-3 sm:p-4 overflow-y-auto pb-28 sm:pb-5">
                    {/* Bloque doctor: foto + nombre + especialidad */}
                    <div className="rounded-2xl bg-slate-50 border-2 border-slate-100 p-4 shrink-0">
                        <div className="flex items-center gap-4">
                            <div className="relative shrink-0">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-[#1f94c2]/10 border-2 border-[#1f94c2]/20 flex items-center justify-center">
                                    {doctorPhoto ? (
                                        <img src={doctorPhoto} alt={doctorName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                    ) : (
                                        <SvgIcon Icon={UsuarioIcon} className="w-10 h-10 sm:w-12 sm:h-12 text-[#1f94c2]" />
                                    )}
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-slate-500 mb-0.5">Tu profesional</p>
                                <h2 className="text-lg sm:text-xl font-bold text-slate-800 truncate">{doctorName}</h2>
                                <p className="text-sm font-medium text-[#1f94c2] mt-0.5">{specialty}</p>
                            </div>
                        </div>
                    </div>

                    {/* Fecha y hora muy visible */}
                    <div className="rounded-2xl bg-gradient-to-br from-[#1f94c2]/15 to-[#205087]/10 border-2 border-[#1f94c2]/25 p-4 sm:p-5 shrink-0">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#1f94c2]/20">
                                <SvgIcon Icon={CalendarioIcon} className="w-5 h-5 text-[#1f94c2]" />
                            </div>
                            <span className="text-sm font-semibold text-slate-600">Fecha y hora de la cita</span>
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                            <span className="text-xl sm:text-2xl font-bold text-slate-800">{fechaFormateada}</span>
                            <span className="text-lg sm:text-xl font-bold text-[#1f94c2]">{horaFormateada}</span>
                        </div>
                        <input type="hidden" name="fecha" value={dataCita.fecha} readOnly />
                    </div>

                    <div className="flex flex-col gap-4 min-w-0">
                            {requiereConfiguracionPrecios() && (() => {
                                const preciosConfig = getPreciosConfig();
                                return (
                                    <div className="rounded-xl bg-amber-50/80 border border-amber-100 p-4 shrink-0">
                                        <h2 className="text-sm font-semibold text-slate-700 mb-3">Tipo de consulta *</h2>
                                        <div className="flex flex-wrap gap-3">
                                            {doctorInfo?.preciosCitas?.teleconsultaPrimeraVez && (
                                                <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-slate-200 bg-white cursor-pointer hover:border-[#1f94c2]/50 has-[:checked]:border-[#1f94c2] has-[:checked]:bg-[#1f94c2]/5 transition-all shrink-0">
                                                    <input type="radio" name="tipoCita" value="teleconsulta_primera_vez" checked={tipoCita === 'teleconsulta_primera_vez'} onChange={(e) => setTipoCita(e.target.value)} className="sr-only" />
                                                    <span className="text-sm font-semibold text-slate-800">Primera vez</span>
                                                    <span className="text-sm font-bold text-emerald-600">${doctorInfo.preciosCitas.teleconsultaPrimeraVez.toLocaleString('es-CO')}</span>
                                                </label>
                                            )}
                                            <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-slate-200 bg-white cursor-pointer hover:border-[#1f94c2]/50 has-[:checked]:border-[#1f94c2] has-[:checked]:bg-[#1f94c2]/5 transition-all shrink-0">
                                                <input type="radio" name="tipoCita" value="controles" checked={tipoCita === 'controles'} onChange={(e) => setTipoCita(e.target.value)} className="sr-only" />
                                                <span className="text-sm font-semibold text-slate-800">Control</span>
                                                <span className="text-sm font-bold text-emerald-600">${(doctorInfo?.preciosCitas?.precioControles || preciosConfig?.precioControles || 0).toLocaleString('es-CO')}</span>
                                            </label>
                                            <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-slate-200 bg-white cursor-pointer hover:border-[#1f94c2]/50 has-[:checked]:border-[#1f94c2] has-[:checked]:bg-[#1f94c2]/5 transition-all shrink-0">
                                                <input type="radio" name="tipoCita" value="teleorientacion" checked={tipoCita === 'teleorientacion'} onChange={(e) => setTipoCita(e.target.value)} className="sr-only" />
                                                <span className="text-sm font-semibold text-slate-800">Teleorientación</span>
                                                <span className="text-sm font-bold text-emerald-600">${(doctorInfo?.preciosCitas?.precioTeleorientacion || preciosConfig?.precioTeleorientacion || 0).toLocaleString('es-CO')}</span>
                                            </label>
                                        </div>
                                        {error && !tipoCita && <p className="text-sm text-red-500 mt-2 font-medium">Selecciona un tipo de consulta</p>}
                                    </div>
                                );
                            })()}

                            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 shrink-0">
                                <h2 className="text-sm font-semibold text-slate-700 mb-3">Forma de pago</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="min-w-0">
                                        <label className="block text-sm font-medium text-slate-600 mb-1.5">Tipo de pagador *</label>
                                        <select name="tipoPagador" value={dataCita.tipoPagador} onChange={handleChange} required className={`${inputBase} bg-white`}>
                                            <option value="" disabled>Selecciona</option>
                                            <option value="particular">Particular</option>
                                            <option value="convenio">Convenio</option>
                                        </select>
                                    </div>
                                    {dataCita.tipoPagador === 'convenio' && (
                                        <div className="min-w-0">
                                            <label className="block text-sm font-medium text-slate-600 mb-1.5">Tipo de convenio *</label>
                                            <select name="tipoConvenio" value={dataCita.tipoConvenio} onChange={handleChange} required className={`${inputBase} bg-white`}>
                                                <option value="" disabled>Selecciona</option>
                                                <option value="cotizante">Cotizante</option>
                                                <option value="beneficiario">Beneficiario</option>
                                                <option value="adicional">Adicional</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="shrink-0">
                                <label className="block text-sm font-medium text-slate-600 mb-1.5">Motivo de consulta *</label>
                                <textarea name="motivoConsulta" value={dataCita.motivoConsulta} onChange={handleChange} rows={3} required placeholder="Describe brevemente el motivo de tu cita..." className={`${inputBase} resize-none`} />
                                {error && <p className="text-sm text-red-500 mt-1">Completa los campos requeridos</p>}
                            </div>

                            <div className="flex items-start gap-3 rounded-xl bg-slate-50/80 border border-slate-100 p-4 shrink-0">
                                <input id="politicas" type="checkbox" checked={aceptaPoliticas} onChange={e => setAceptaPoliticas(e.target.checked)} className="mt-1 w-4 h-4 rounded border-slate-300 text-[#1f94c2] focus:ring-[#1f94c2]" />
                                <label htmlFor="politicas" className="text-sm text-slate-600">
                                    Acepto las <Link to="/terminos-y-condiciones" state={{ returnTo: '/patient/agendar-cita' }} className="text-[#1f94c2] font-semibold underline hover:no-underline">políticas de privacidad de Redplika</Link> <span className="text-red-500">*</span>
                                </label>
                            </div>
                            {intentoEnviar && !aceptaPoliticas && <p className="text-sm text-red-500 font-medium">Debes aceptar las políticas para continuar</p>}

                            <div className="flex flex-col-reverse sm:flex-row gap-3 shrink-0 pt-2 pb-4 sm:pb-0">
                                <button type="button" onClick={handleClickRegresar} className="w-full sm:flex-1 px-4 py-3.5 text-sm font-semibold rounded-xl border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 touch-manipulation">
                                    Regresar
                                </button>
                                <div className="w-full sm:flex-1 min-w-0">
                                    <BotonSubmitEpayco
                                    doctorId={dataCita.doctorId}
                                    descripcion={"Cita con " + dataCita.doctor + " - Motivo: " + dataCita.motivoConsulta}
                                    redireccion={APP_URLS.aprobacionCitaEpayco(role)}
                                    correo={userEmail}
                                    amount={precioCita}
                                    invoice={dataCita.fecha}
                                    disabled={error || !aceptaPoliticas}
                                    extraData={{
                                        x_extra1: "cita",
                                        x_extra2: dataCita.doctorId,
                                        x_extra3: patientId,
                                        x_extra4: dataCita.doctor,
                                        x_extra5: fullName,
                                        x_extra6: dataCita.motivoConsulta,
                                        x_extra7: dataCita.fecha,
                                        x_extra8: dataCita.tipoPagador || 'particular',
                                        x_extra9: dataCita.tipoConvenio || 'sin convenio',
                                        x_extra10: dataCita.tipoCita || tipoCita || '',
                                        servicio: dataCita.servicio,
                                        motivoConsulta: dataCita.motivoConsulta,
                                        tipoPagador: dataCita.tipoPagador,
                                        tipoConvenio: dataCita.tipoConvenio,
                                        tipoCita: dataCita.tipoCita || tipoCita
                                    }}
                                    onClick={() => setIntentoEnviar(true)}
                                />
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
        </div>
    );
};

export default AgendarCita;